import { cva } from 'class-variance-authority';

import {
  textScaleVariants,
  buttonHeightVariants,
  paddingScaleVariants,
  borderRadiusVariants,
  focusRingClasses,
  disabledStateClasses,
  fontWeightVariants,
  widthVariants,
} from '@/lib/shared-variants';

/**
 * Split Button container styles
 * Container for split button with primary action and dropdown
 * Based on Figma Design System Nabat 2.0 - Split Button
 */
export const splitButtonContainerVariants = cva('inline-flex items-stretch', {
  variants: {
    width: {
      auto: 'w-auto',
      full: 'w-full',
    },
  },
  defaultVariants: {
    width: 'auto',
  },
});

/**
 * Split Button primary button styles
 * Main action button (left side)
 */
export const splitButtonPrimaryVariants = cva(
  `inline-flex items-center justify-center gap-xs ${fontWeightVariants.medium} transition-all ${focusRingClasses} ${disabledStateClasses} rounded-r-none border-r-0`,
  {
    variants: {
      appearance: {
        solid: '',
        outline: 'border-t border-b border-l',
        transparent: 'bg-transparent',
      },
      kind: {
        neutral: '',
        brand: '',
        danger: '',
        inverse: '',
      },
      scale: {
        s: `${buttonHeightVariants.s} ${paddingScaleVariants.s} ${textScaleVariants.s} rounded-l-${borderRadiusVariants.sm}`,
        m: `${buttonHeightVariants.m} ${paddingScaleVariants.m} ${textScaleVariants.m} rounded-l-${borderRadiusVariants.sm}`,
        l: `${buttonHeightVariants.l} ${paddingScaleVariants.l} ${textScaleVariants.l} rounded-l-${borderRadiusVariants.sm}`,
      },
    },
    compoundVariants: [
      // Solid appearance variants
      {
        appearance: 'solid',
        kind: 'neutral',
        className:
          'bg-surface-subtle text-foreground hover:bg-surface-subtle/90 active:bg-surface-subtle/80',
      },
      {
        appearance: 'solid',
        kind: 'brand',
        className: 'bg-brand text-white hover:bg-brand/90 active:bg-brand/80',
      },
      {
        appearance: 'solid',
        kind: 'danger',
        className:
          'bg-status-danger text-white hover:bg-status-danger/90 active:bg-status-danger/80',
      },
      {
        appearance: 'solid',
        kind: 'inverse',
        className: 'bg-shades-basic text-white hover:bg-shades-basic/90 active:bg-shades-basic/80',
      },

      // Outline appearance variants
      {
        appearance: 'outline',
        kind: 'neutral',
        className: 'border-outline text-foreground hover:bg-surface-muted active:bg-surface-subtle',
      },
      {
        appearance: 'outline',
        kind: 'brand',
        className: 'border-brand text-brand hover:bg-brand/10 active:bg-brand/20',
      },
      {
        appearance: 'outline',
        kind: 'danger',
        className:
          'border-status-danger text-status-danger hover:bg-status-danger/10 active:bg-status-danger/20',
      },
      {
        appearance: 'outline',
        kind: 'inverse',
        className: 'border-white text-white hover:bg-white/10 active:bg-white/20',
      },
    ],
    defaultVariants: {
      appearance: 'solid',
      kind: 'neutral',
      scale: 'm',
    },
  }
);

/**
 * Split Button dropdown trigger styles
 * Dropdown toggle button (right side)
 */
export const splitButtonDropdownVariants = cva(
  `inline-flex items-center justify-center transition-all ${focusRingClasses} ${disabledStateClasses} rounded-l-none border-l`,
  {
    variants: {
      appearance: {
        solid: '',
        outline: 'border-t border-b border-r',
        transparent: 'bg-transparent',
      },
      kind: {
        neutral: '',
        brand: '',
        danger: '',
        inverse: '',
      },
      scale: {
        s: `${widthVariants[6]} ${buttonHeightVariants.s} rounded-r-${borderRadiusVariants.sm}`,
        m: `${widthVariants[8]} ${buttonHeightVariants.m} rounded-r-${borderRadiusVariants.sm}`,
        l: `${widthVariants[11]} ${buttonHeightVariants.l} rounded-r-${borderRadiusVariants.sm}`,
      },
    },
    compoundVariants: [
      // Solid appearance variants
      {
        appearance: 'solid',
        kind: 'neutral',
        className:
          'bg-surface-subtle text-foreground hover:bg-surface-subtle/90 active:bg-surface-subtle/80 border-outline',
      },
      {
        appearance: 'solid',
        kind: 'brand',
        className: 'bg-brand text-white hover:bg-brand/90 active:bg-brand/80 border-white/20',
      },
      {
        appearance: 'solid',
        kind: 'danger',
        className:
          'bg-status-danger text-white hover:bg-status-danger/90 active:bg-status-danger/80 border-white/20',
      },
      {
        appearance: 'solid',
        kind: 'inverse',
        className:
          'bg-shades-basic text-white hover:bg-shades-basic/90 active:bg-shades-basic/80 border-white/20',
      },

      // Outline appearance variants
      {
        appearance: 'outline',
        kind: 'neutral',
        className: 'border-outline text-foreground hover:bg-surface-muted active:bg-surface-subtle',
      },
      {
        appearance: 'outline',
        kind: 'brand',
        className: 'border-brand text-brand hover:bg-brand/10 active:bg-brand/20',
      },
      {
        appearance: 'outline',
        kind: 'danger',
        className:
          'border-status-danger text-status-danger hover:bg-status-danger/10 active:bg-status-danger/20',
      },
      {
        appearance: 'outline',
        kind: 'inverse',
        className: 'border-white text-white hover:bg-white/10 active:bg-white/20',
      },
    ],
    defaultVariants: {
      appearance: 'solid',
      kind: 'neutral',
      scale: 'm',
    },
  }
);
